import i18n, { N_ } from './index';
import { SOURCE_LANGUAGE } from './languages';

/** Language-neutral text kept with a stored record (backend/src/i18n/record.js) */
export interface TextRecord {
  v: number;
  k: string;
  p?: Record<string, unknown>;
}

function render(record: TextRecord): string {
  const params: Record<string, unknown> = {};
  for (const [name, value] of Object.entries(record.p || {})) {
    const obj = value && typeof value === 'object' ? (value as { $t?: unknown; k?: unknown; v?: unknown }) : null;
    if (obj && obj.v === 1 && typeof obj.k === 'string') params[name] = render(obj as TextRecord); // a clause
    else params[name] = obj && typeof obj.$t === 'string' ? i18n.t(obj.$t) : value;
  }
  return i18n.t(record.k, params);
}

/**
 * Text of a stored record in the UI language. Turkish shows the stored text as it is; another
 * language renders the record when there is one. Records written before this existed have only
 * the stored text, which is shown unchanged (it may be a customer's own words).
 */
export function storedText(text: string | null | undefined, record?: TextRecord | null): string {
  if (record && typeof record.k === 'string' && i18n.language !== SOURCE_LANGUAGE) return render(record);
  return text ?? '';
}

// Stored texts that join a fixed Turkish lead with another text (backend social connect.js and
// services/meta/errors.js); the rest is a stored text itself or Meta's own words
const META_COMPOSITES: Array<[RegExp, string, string]> = [
  [/^Bildirim aboneliği yapılamadı: ([\s\S]*)$/, N_('Bildirim aboneliği yapılamadı: {{reason}}'), 'reason'],
  [/^Meta isteği reddetti: ([\s\S]*)$/, N_('Meta isteği reddetti: {{message}}'), 'message'],
];

/**
 * A Turkish status or error text stored on Meta accounts and messages (status_detail,
 * profile_error, error_message) in the UI language. Unknown texts (Meta's own words, texts
 * cut at the column length) are shown unchanged.
 */
export function metaText(text: string | null | undefined): string {
  if (!text) return '';
  if (i18n.language === SOURCE_LANGUAGE) return text;
  for (const [pattern, key, name] of META_COMPOSITES) {
    const match = text.match(pattern);
    if (match) return i18n.t(key, { [name]: metaText(match[1]) });
  }
  return i18n.exists(text) ? i18n.t(text) : text;
}
